// ── Task board page ──

import { api } from '../api.js';
import { checkAuth, getUser } from '../auth.js';
import { showToast, closeModal } from '../ui.js';
import { escapeHtml } from '../utils.js';
import { initLayout, getContentEl, updateLayoutContext } from '../layout.js';
import { renderViewSwitcher, bindViewSwitcher, getViewPref, setViewPref, updateViewSwitcherLabel, removeViewSwitcherOption, setViewSwitcherActive } from '../components/view-switcher.js';
import { openCreateTaskModal as showCreateTaskModal } from '../components/create-task-modal.js';
import { renderBoardFilters, bindBoardFilters, unbindBoardFilters, hasActiveFilters, hasFilterParams, filtersToParams, filtersFromParams, filtersToQuery, FILTER_DEFAULTS, sprintFilterNumbers, applySprintPresets, syncFilterChips, syncEditBoardButton, syncStatusCounts, captureFilterFocus, restoreFilterFocus } from '../components/board-filters.js';
import { getQueryParams, setQueryParams, getNavigationId } from '../router.js';
import { saveFilterPrefs, loadFilterPrefs, clearFilterPrefs } from '../lib/board-filter-prefs.js';
import { renderKanbanView } from './kanban.js';
import { renderSprintView } from './sprint-board.js';
import { renderGanttView } from './gantt.js';
import { openSprintManager } from '../components/sprint-manager.js';
import { openTrelloImportModal } from '../components/trello-import-modal.js';
import { loadStatuses, statusNames } from '../lib/statuses.js';
import { loadProject } from '../lib/project.js';
import { can, applyWriteGate } from '../lib/permissions.js';
import { groupLabelPlural, groupLabelSingular } from '../lib/task-group-label.js';
import { sprintsEnabled } from '../lib/sprints-enabled.js';
import { createBulkSelection, renderBulkToolbar } from '../components/bulk-actions.js';
import { deviceSessionClient } from '../lib/device-session-client.js';
import { createDeviceSessionScope, applyDeviceSessionEvent, syncDeviceBadges } from '../lib/device-attribution.js';

// Filter-bar options from GET /tasks/facets (TPT596). The board holds only its filtered rows
// (TPT597), so the tag list, the in-use sprint numbers and the per-status counts come from
// here, never from state.tasks. Never mutated — normalizeFacets() builds a new object.
const EMPTY_FACETS = { tags: [], priorities: [], statusCounts: { all: 0 } };

function freshState() {
  return {
    projectId: null,
    navId: null,        // router navigation id of the mount (see boardPage())
    project: null,
    tasks: [],          // GET /tasks rows for the current filters (fields=board), not the whole project
    members: [],
    sprints: [],
    statuses: [],
    filters: { ...FILTER_DEFAULTS },
    facets: EMPTY_FACETS,
    facetsKey: null,    // the facets query state.facets answers (see loadBoardTasks())
    loadSeq: 0,         // bumped by every loadBoardTasks() call; a superseded reply is dropped
    loaded: false,      // a task list has loaded at least once this mount
    loadError: null,    // message shown in place of the board after a failed load
    activeView: 'sprint',
    subViewCleanup: null,
    deviceScope: null,
    unwatchDevices: null,
    // Edit Board (C1183) — kanban column-header drag reorder, off by default every mount.
    editBoardMode: false,
    filterBinding: null, // token of the latest bindBoardFilters() (TPT643), passed to the teardown's unbind
    modalToken: null,    // owner token of the latest modal this page opened (TPT647), passed to the teardown's closeModal()
    // (TPT9099) Ticked cards. Owned here, not by the card views: renderActiveView() re-mounts the
    // view on every reload, and the selection must outlive that so bulk actions chain. Cleared on a
    // view switch and a filter change; a new mount (another project) starts empty.
    selection: createBulkSelection(),
  };
}

let state = freshState();

function resetState() {
  state = freshState();
}

// ── Bulk-action toolbar (TPT9099) ──
// components/bulk-actions.js over state.selection, in #board-bulk-host — outside everything
// renderActiveView() rebuilds, so a focused toolbar field and an in-flight request both survive a
// reload. The host shares #board-controls' one grid cell with the filter bar and covers it while
// cards are ticked (styles.css § Board card multi-select): the board below never jumps when the
// first card is ticked, and the filters a change would clear the selection with are out of reach.
// Module-scoped like backlog.js's: its member combo and tag typeaheads own window listeners that
// leak unless destroy() runs. Created once per mount for a writer; destroyed by the teardown or by
// the next mount.
let bulkToolbar = null;

// Reordering the workflow from the board needs statuses:manage, the capability the API's
// PUT /statuses/reorder enforces. False until the project (and its capabilities) loads.
function canEditWorkflow() {
  return can(state.project, getUser(), 'statuses:manage');
}

// Creating, moving and reassigning tasks needs task:write. False until the project loads,
// so nothing write-shaped renders before access is known.
function canWriteTasks() {
  return can(state.project, getUser(), 'task:write');
}

// C1334 — project.sprints_enabled gate. Safe before the project fetch resolves
// (state.project === null → sprintsEnabled() reads that as ON, see lib/sprints-enabled.js).
function sprintsOn() {
  return sprintsEnabled(state.project);
}

function normalizeFacets(res) {
  return {
    tags: Array.isArray(res && res.tags) ? res.tags : [],
    priorities: Array.isArray(res && res.priorities) ? res.priorities : [],
    statusCounts: (res && res.status_counts) || { all: 0 },
  };
}

// The sprint selector (sprintFilterNumbers()) reads sprint numbers off task rows' `priority`.
// These `{ priority }` rows stand in for the project-wide task list the board no longer holds —
// facets.priorities ignores every filter.
function facetSprintRows(facets) {
  return facets.priorities.map(priority => ({ priority }));
}

// Dims the board while its rows are being fetched. styles.css delays the fade, so a fast
// reply never flickers.
function setViewportBusy(busy) {
  const viewport = document.getElementById('board-viewport');
  if (!viewport) return;
  if (busy) viewport.setAttribute('aria-busy', 'true');
  else viewport.removeAttribute('aria-busy');
}

// ── Create task modal ──

// Every board entry point — the header's + New Task, a sprint lane's + Add item (`priority`), a
// kanban column's + (`status`) — opens the full New Task form: one control per field
// taskEditableFields() lists (lib/task-fields.js, TPT9213), the same set the task drawer edits.
// This wrapper only supplies the board's cached context and its reload.
function openCreateTaskModal(opts = {}) {
  const owner = state;
  const opened = showCreateTaskModal(owner.projectId, {
    project: owner.project,
    // No task rows (TPT598): the modal takes its sprint numbers from GET /tasks/facets and its
    // Depends on candidates from an on-demand server search, never from the filtered page.
    members: owner.members,
    sprints: owner.sprints,
    statuses: owner.statuses,
    isAlive: () => state === owner,
    onCreated: () => loadBoardTasks(),
    ...opts,
  });
  opened.then((token) => { if (token && state === owner) owner.modalToken = token; });
  return opened;
}

// ── Filter bar ──

function onFilterChange(filters, changedKey) {
  state.filters = filters;
  // (TPT9099) A new selection of rows is a new board: ticks made against the old rows go.
  state.selection.clear();
  setQueryParams(filtersToParams(filters));
  // TPT25 — Clear All means "forget this project's saved selection too", every other
  // mutation (including the now-empty state a single chip-remove can produce) updates it.
  if (changedKey === 'clear') clearFilterPrefs(state.projectId);
  else saveFilterPrefs(state.projectId, filters);
  // C1524 — a search-only change must not rebuild #board-filters: #bf-search is focused
  // and mid-typing, and the innerHTML wipe would take the caret (and any untrimmed
  // trailing space) with it. The chip row + Clear All are the only bar content filters.q
  // affects.
  if (changedKey === 'q') syncFilterChips(state.filters, state.members, state.sprints, filterOpts());
  else refreshFilterBar();
  // TPT597 — the rows come from the server: refetch for the new selection. A search reaches
  // this already debounced (300ms, board-filters.js's search handler).
  loadBoardTasks({ filtersChanged: true });
}

// Shared by refreshFilterBar() and the q-only syncFilterChips() path (C1524) so both agree
// on the options renderBoardFilters/renderFilterChips render against.
function filterOpts() {
  const plural = groupLabelPlural(state.project && state.project.task_group_label);
  const singular = groupLabelSingular(state.project && state.project.task_group_label);
  return { language: state.project?.language, activeView: state.activeView, editBoardMode: state.editBoardMode, canEditWorkflow: canEditWorkflow(), groupLabel: plural, groupLabelSingular: singular, sprintsEnabled: sprintsOn() };
}

function onToggleEditBoard() {
  state.editBoardMode = !state.editBoardMode;
  // TPT9059 — patch the button's pressed state in place, not refreshFilterBar(): the click just
  // moved focus off #bf-search, so a full rebuild would reset the input to the committed
  // filters.q and drop anything typed inside its 300ms debounce.
  syncEditBoardButton(state.filters, filterOpts());
  renderActiveView();
}

// Bubbles a successful column-reorder drop (kanban.js) back up so the next
// refreshFilterBar()/renderActiveView() pass (e.g. switching view, editing a status
// name elsewhere) sees the fresh display_order — no extra round-trip needed, the
// reorder endpoint already returns the full ordered list.
function onStatusesChanged(newStatuses) {
  state.statuses = newStatuses || state.statuses;
}

// Loads the board's rows for the current filters (TPT597): GET /tasks with filtersToQuery()'s
// params — never the whole project — plus GET /tasks/facets for the filter bar's options.
//
// - No argument: page load and every "server data changed" callback (task-detail drawer close,
//   a created task, the Trello import, a failed drag-save). Always refetches the facets and
//   rebuilds the whole bar, since a new tag or sprint may now exist.
// - { filtersChanged: true } from onFilterChange(): the bar already shows the new selection
//   (rebuilt, or chip-synced for a search — C1524), so only the status counts are patched in
//   place (syncStatusCounts()). The facets are refetched only when a non-status dimension
//   changed: status never narrows them (TPT596), so their key is the query without it.
//
// Sub-views receive state.tasks itself and must never refetch into it or push/splice it
// (C1261) — they call options.onDataChanged, which lands here. Every call bumps loadSeq; a reply
// that lands after a newer call, after the page was re-mounted (resetState() swaps the state
// object), or after any navigation since the mount (the page is gone) is dropped.
async function loadBoardTasks({ filtersChanged = false } = {}) {
  const owner = state;
  // task-detail's onClose fires ~220ms after close (see task-detail.js) — can land after
  // boardPage()'s teardown already ran resetState(). Bail instead of GET /projects/null/tasks.
  if (!owner.projectId) return;
  const seq = ++owner.loadSeq;
  const isCurrent = () => state === owner && owner.loadSeq === seq && getNavigationId() === owner.navId;
  const base = '/projects/' + owner.projectId + '/tasks';
  const facetsKey = filtersToQuery({ ...owner.filters, statuses: [] }).toString();
  const needFacets = !filtersChanged || facetsKey !== owner.facetsKey;

  setViewportBusy(true);
  let tasksRes;
  let facetsRes = null;
  try {
    [tasksRes, facetsRes] = await Promise.all([
      api.get(base + '?' + filtersToQuery(owner.filters)),
      // Not fatal: without a fresh reply the bar keeps its last options and counts.
      needFacets ? api.get(base + '/facets?' + facetsKey).catch(() => null) : null,
    ]);
  } catch (err) {
    if (!isCurrent()) return;
    setViewportBusy(false);
    // A data reload keeps the rows it has — they still match the bar. A new selection (or the
    // first load) has no valid rows to show, e.g. a 400 for a status renamed since page load
    // or a hand-edited `?type=` value.
    if (filtersChanged || !owner.loaded) {
      owner.loadError = (err && err.message) || 'Request failed';
      // The first load has no filter bar yet: render it anyway, so the selection that failed
      // can be changed or cleared (chips, Clear All). A filter change already rebuilt it.
      if (!filtersChanged) refreshFilterBar();
      renderActiveView();
    }
    return;
  }
  if (!isCurrent()) return;

  owner.tasks = (tasksRes && tasksRes.tasks) || [];
  if (owner.deviceScope) applyDeviceSessionEvent(owner.tasks, { type: 'reconcile' }, owner.deviceScope);
  owner.loaded = true;
  owner.loadError = null;
  if (facetsRes) {
    owner.facets = normalizeFacets(facetsRes);
    owner.facetsKey = facetsKey;
  }
  setViewportBusy(false);
  if (filtersChanged) syncStatusCounts(owner.filters, owner.facets.statusCounts);
  else refreshFilterBar();
  renderActiveView();
}

function refreshFilterBar() {
  const el = document.getElementById('board-filters');
  if (!el) return;
  // TPT597 — options come from GET /tasks/facets: state.tasks is only the filtered page, so
  // reading tags/sprints/counts off it would collapse every option list to the selection.
  const allTags = state.facets.tags;
  const sprintRows = facetSprintRows(state.facets);
  const opts = { ...filterOpts(), statusCounts: state.facets.statusCounts };
  // C1524 — every other trigger of this full rebuild (status/type/assignee/tag/sprint
  // toggles, Clear All, loadBoardTasks()'s ~220ms-delayed drawer-close reload, sprint
  // manager updates, create-task success, page load) can land while #bf-search is
  // focused mid-keystroke. Capture before the innerHTML wipe, restore after rebind.
  const focusSnap = captureFilterFocus();
  el.innerHTML = renderBoardFilters(state.filters, state.members, allTags, state.sprints, sprintRows, state.statuses, opts);
  state.filterBinding = bindBoardFilters(state.filters, onFilterChange, state.members, allTags, state.sprints, sprintRows, state.statuses, opts, onToggleEditBoard);
  restoreFilterFocus(focusSnap);
}

// ── View switching ──

function renderActiveView() {
  if (state.subViewCleanup) {
    state.subViewCleanup();
    state.subViewCleanup = null;
  }

  const viewport = document.getElementById('board-viewport');
  if (!viewport) return;
  viewport.innerHTML = '';

  // (TPT9099) Nothing stays ticked that the board no longer holds — a task a reload dropped, or
  // every task while a load error, a spinner or an empty state shows instead of a card view. The
  // card views narrow this further to the cards they draw.
  const onBoard = new Set(state.loadError ? [] : state.tasks.map(t => t.task_key));
  state.selection.retain(key => onBoard.has(key));
  // Sprint numbers in use may have changed with the rows.
  if (bulkToolbar) bulkToolbar.refresh();

  // (TPT112) #content is the same <main> element for the life of the SPA session
  // (layout.js's initLayout() early-returns once the shell is mounted), so this
  // class must be kept in sync with the active view here AND explicitly removed
  // in boardPage()'s teardown below — a leaked class would turn every other page
  // into a height-bounded flex column. Scoped to kanban only: the sprint view
  // renders into this same #board-viewport and must keep growing the page, and the
  // Gantt view (TPT591) sizes its own scroller instead.
  getContentEl().classList.toggle('board-page--kanban', state.activeView === 'kanban');

  if (state.loadError) {
    viewport.innerHTML = `<div class="empty text-muted">Failed to load tasks: ${escapeHtml(state.loadError)}</div>`;
    return;
  }
  // A view switch before the first load lands must not claim the project is empty.
  if (!state.loaded) {
    viewport.innerHTML = '<div class="empty"><span class="spinner"></span></div>';
    return;
  }

  const filtersActive = hasActiveFilters(state.filters);

  // TPT597 — state.tasks is the server-filtered page. facets.priorities is project-wide and
  // every task has a priority >= 0, so an empty list there is what "no tasks yet" means.
  if (state.tasks.length === 0 && state.facets.priorities.length === 0) {
    viewport.innerHTML = '<div class="empty">No tasks yet. Create one to get started.</div>';
    return;
  }
  // Kanban must keep rendering all five status columns even when the current
  // sprint selection matches zero tasks, so every status stays a valid
  // drag-and-drop / quick-create target (C1042). The sprint selector itself
  // lives in the always-visible filter bar above #board-viewport (C1166), so
  // this exception is no longer about selector reachability.
  if (state.tasks.length === 0 && state.activeView !== 'kanban') {
    viewport.innerHTML = '<div class="empty">No tasks match your filters.</div>';
    return;
  }

  // Project-wide in-use sprint numbers for the task drawer's Sprint select (TPT628) — its caller
  // rows are only this filtered page.
  const sprintRows = facetSprintRows(state.facets);
  // (TPT9099) The card views draw checkboxes over this selection — a writer's only, like the
  // toolbar that acts on it.
  const selection = canWriteTasks() ? state.selection : null;

  switch (state.activeView) {
    case 'kanban':
      state.subViewCleanup = renderKanbanView(viewport, state.tasks, state.projectId, state.members, state.statuses, {
        language: state.project?.language, projectPermissionMode: state.project?.agent_permission_mode,
        editBoardMode: state.editBoardMode && canEditWorkflow(),
        onStatusesChanged,
        onDataChanged: () => loadBoardTasks(),
        sprintRows,
        onAddTask: (status) => openCreateTaskModal({ status }),
        canWrite: canWriteTasks(),
        selection,
      });
      break;
    case 'gantt':
      state.subViewCleanup = renderGanttView(viewport, state.tasks, state.sprints, {
        projectId: state.projectId,
        statuses: state.statuses,
        sprintRows,
        groupLabel: groupLabelSingular(state.project && state.project.task_group_label),
        groupLabelPlural: groupLabelPlural(state.project && state.project.task_group_label),
        onDataChanged: () => loadBoardTasks(),
        canWrite: canWriteTasks(),
      });
      break;
    case 'sprint':
    default: {
      const groupLabel = groupLabelSingular(state.project && state.project.task_group_label);
      state.subViewCleanup = renderSprintView(viewport, state.tasks, state.projectId, state.members, state.sprints, { filtersActive, groupLabel, language: state.project?.language, projectPermissionMode: state.project?.agent_permission_mode, onDataChanged: () => loadBoardTasks(), sprintRows, onAddTask: (priority) => openCreateTaskModal({ priority }), sprintsEnabled: sprintsOn(), canWrite: canWriteTasks(), selection }, state.statuses);
      break;
    }
  }
}

function onViewChange(viewType) {
  state.activeView = viewType;
  setViewPref(state.projectId, viewType);
  // TPT9059 — Edit Board is a kanban-only mode: leaving kanban turns it off, so a return to
  // kanban never arrives with column drag handles already armed and no pressed button to explain
  // them. The bar's kanban-only button then follows the view in place (hidden/pressed state) —
  // never refreshFilterBar() here: a full rebuild drops search text typed inside #bf-search's
  // 300ms debounce. Before the first bar render there is nothing to patch; that render reads
  // state.activeView itself.
  if (viewType !== 'kanban') state.editBoardMode = false;
  // (TPT9099) Ticks are made in one view's layout (a column, a lane); none carry into another.
  state.selection.clear();
  syncEditBoardButton(state.filters, filterOpts());
  renderActiveView();
}

// ── Page entry point ──

export async function boardPage(params) {
  // Read before the first await: once it changes, the user navigated on and this mount stops
  // after its next await — no state write, render, binding or URL write for a page that is gone.
  const navId = getNavigationId();
  const superseded = () => getNavigationId() !== navId;
  if (!(await checkAuth()) || superseded()) return;
  state.unwatchDevices?.();
  resetState();
  const owner = state;
  state.navId = navId;
  state.projectId = params.id;
  state.activeView = getViewPref(params.id);

  initLayout(getUser());
  updateLayoutContext({ projectId: params.id, projectName: null, activeView: 'board', viewLabel: 'Board' });

  const content = getContentEl();
  content.innerHTML = `
    <div class="page-header board-header access-pending" id="board-header">
      <h2>Board</h2>
      ${renderViewSwitcher(state.activeView)}
      <button class="btn btn-ghost btn-sm needs-write" id="manage-sprints-btn">Sprints</button>
      <button class="btn btn-ghost btn-sm needs-write" id="trello-import-btn">Import from Trello</button>
      <button class="btn btn-primary btn-sm needs-write" id="new-task-btn">+ New Task</button>
    </div>
    <div id="board-controls">
      <div id="board-filters"></div>
      <div id="board-bulk-host"></div>
    </div>
    <div id="board-viewport"><div class="empty"><span class="spinner"></span></div></div>`;

  // Header button handlers
  bindViewSwitcher(onViewChange);
  document.getElementById('new-task-btn').addEventListener('click', () => openCreateTaskModal());
  document.getElementById('trello-import-btn').addEventListener('click', () => {
    state.modalToken = openTrelloImportModal(state.projectId, () => loadBoardTasks());
  });

  document.getElementById('manage-sprints-btn').addEventListener('click', () => {
    state.modalToken = openSprintManager(state.projectId, {
      // (C1395) resolved from state.project (fetched below, may still be null on a very
      // fast click before the page-load Promise.all resolves) — openSprintManager()
      // itself defaults to 'Sprint'/'Sprints' when omitted.
      groupLabel: {
        plural: groupLabelPlural(state.project && state.project.task_group_label),
        singular: groupLabelSingular(state.project && state.project.task_group_label),
      },
      onUpdate: async () => {
        try {
          const res = await api.get('/projects/' + state.projectId + '/sprints');
          state.sprints = res.sprints || [];
          // Sprint names/closed-status live in the selector's option labels now
          // (moved off the kanban sub-view in C1166) — refresh the bar too, or
          // a rename/close here leaves stale labels/dimming in the dropdown.
          refreshFilterBar();
          renderActiveView();
        } catch (_) {}
      },
    });
  });

  try {
    // TPT597 — no task fetch here: the rows depend on the restored filters below, so
    // loadBoardTasks() fetches them (and the filter-bar facets) once those are known.
    const [membersRes, sprintsRes, statuses, project] = await Promise.all([
      api.get('/projects/' + params.id + '/members'),
      api.get('/projects/' + params.id + '/sprints'),
      loadStatuses(params.id),
      // C1234/C1395 — task_group_label for board title bars, via the shared per-project
      // cache (lib/project.js) so a Settings-page save's invalidateProject() is visible
      // here on the next visit without a hard reload. Fails open (null) on its own, so a
      // fetch hiccup falls back to the default label instead of killing the whole board.
      loadProject(params.id),
    ]);
    if (superseded()) return;
    state.members = membersRes.members || [];
    state.sprints = sprintsRes.sprints || [];
    state.statuses = statuses || [];
    state.project = project;

    // Header write controls (New Task, Import from Trello, the sprint manager) settle
    // now that access is known — removed outright without task:write.
    applyWriteGate(document.getElementById('board-header'), canWriteTasks());

    // (TPT9099) The bulk-action toolbar over the ticked cards, for a writer only (a viewer gets
    // neither checkboxes nor toolbar). Every action changes the whole selection in one request,
    // and the server re-reads what it changed — a cascade can move tasks beyond the selection —
    // so onApplied reloads through the owner, re-counting the filter bar as well.
    if (bulkToolbar) { bulkToolbar.destroy(); bulkToolbar = null; } // a mount whose teardown has not run yet
    if (canWriteTasks()) {
      bulkToolbar = renderBulkToolbar(document.getElementById('board-bulk-host'), {
        selection: state.selection,
        projectId: params.id,
        getProject: () => state.project, // sprints off (C1334) → no Sprint control
        getMembers: () => state.members,
        getSprints: () => state.sprints,
        getSprintRows: () => facetSprintRows(state.facets),
        getTask: key => state.tasks.find(t => t.task_key === key),
        onApplied: () => loadBoardTasks(),
        onFailed: () => loadBoardTasks(), // an earlier chunk may have applied: the server is the truth
      });
    }

    // Header button label follows the project's task_group_label (C1234) — set
    // after the fetch resolves since the header HTML above renders before it does.
    // C1334 — sprints hidden: drop the button entirely. `.remove()`, not `.hidden` — `.btn
    // { display: inline-flex }` (styles.css) out-specifies the UA `[hidden]` rule (same
    // trap `.pwa-install-btn[hidden]`/`.settings-pane[hidden]` work around).
    const manageBtn = document.getElementById('manage-sprints-btn');
    if (manageBtn) {
      if (!sprintsOn()) {
        manageBtn.remove();
      } else {
        manageBtn.textContent = groupLabelPlural(state.project && state.project.task_group_label);
      }
    }
    // (C1395) view-switcher's 'Sprint Board' tooltip — same late-refresh reason as the
    // manage-sprints button above (rendered before this fetch resolves).
    updateViewSwitcherLabel(groupLabelSingular(state.project && state.project.task_group_label));
    // C1334 — the Gantt view is a sprint timeline (TPT591), so it goes with the sprints. A saved
    // 'gantt' pref shows the Sprint Board (flat) for this mount and is kept as it is, so turning
    // sprints back on returns to the Gantt. Before loadBoardTasks(): its filter bar reads activeView.
    if (!sprintsOn()) {
      removeViewSwitcherOption('gantt');
      if (state.activeView === 'gantt') {
        state.activeView = 'sprint';
        setViewSwitcherActive('sprint');
      }
    }

    // Restore filters — TPT25: an explicit URL query wins (shareable link); a bare hash
    // (every route navigation drops the query entirely — see router.js's resolve()) falls
    // back to this project's last saved selection instead of resetting to defaults.
    const queryParams = getQueryParams();
    const sourceParams = hasFilterParams(queryParams) ? queryParams : (loadFilterPrefs(state.projectId) || queryParams);
    state.filters = { ...FILTER_DEFAULTS, ...filtersFromParams(sourceParams) };

    // A saved/URL status name this project's workflow no longer has (renamed/deleted since
    // it was written) would otherwise filter the board to zero rows with no checkbox left
    // to explain why — drop anything not in the freshly-loaded status list. (Since TPT597 the
    // API would also reject it with a 400.)
    const knownStatusNames = new Set(statusNames(state.statuses));
    state.filters.statuses = state.filters.statuses.filter(s => knownStatusNames.has(s));

    if (!sprintsOn()) {
      // C1334 — sprints hidden: the flat list must be the WHOLE list. Force "all" even when
      // the source carries an explicit sprint selection (stale bookmark/pref, or the toggle
      // flipped in another tab) — with the selector and chips gated out of the filter bar
      // there is no control left to clear it with, and a silently-truncated "flat list" is a
      // lie. `[]` serializes as the explicit `sprint=all` sentinel, so a reload stays stable.
      state.filters.sprints = [];
    } else if (!sourceParams.has('sprint')) {
      // No sprint selection anywhere (URL nor saved pref) → default to the 5 most recent
      // sprints instead of showing everything unfiltered. The in-use sprint numbers come from
      // GET /tasks/facets (TPT597) — `priorities` ignores every filter, so a bare call is
      // enough; if it fails, the sprint table alone still yields a default.
      const facets = await api.get('/projects/' + params.id + '/tasks/facets').catch(() => null);
      if (superseded()) return;
      if (facets) state.facets = normalizeFacets(facets);
      state.filters.sprints = applySprintPresets(sprintFilterNumbers(state.sprints, facetSprintRows(state.facets)), 'recent');
    }
    // Write back so the URL reflects what's actually shown (shareable) — covers both
    // branches above plus a plain "restored from saved prefs, URL was bare" mount.
    setQueryParams(filtersToParams(state.filters));

    // Rows + facets for the restored selection, then the filter bar + board. Handles its own
    // failure (renders it in the viewport).
    await loadBoardTasks();
    if (superseded()) return;
    owner.deviceScope = createDeviceSessionScope(owner.projectId);
    owner.unwatchDevices = deviceSessionClient.watchProject(Number(owner.projectId), event => {
      if (state !== owner || getNavigationId() !== owner.navId) return;
      if (applyDeviceSessionEvent(owner.tasks, event, owner.deviceScope)) {
        syncDeviceBadges(document.getElementById('board-viewport'), owner.tasks, owner.project?.language);
      }
    });
  } catch (err) {
    if (superseded()) return;
    applyWriteGate(document.getElementById('board-header'), false);
    document.getElementById('board-viewport').innerHTML =
      `<div class="empty text-muted">Failed to load tasks</div>`;
  }

  return () => {
    owner.unwatchDevices?.();
    // A late teardown (router.js runs it as soon as it resolves) must leave a newer
    // boardPage() alone: its state, filter bar, drawer and kanban class are not ours.
    if (state !== owner) return;
    unbindBoardFilters(state.filterBinding);
    // The sub-view's cleanup closes the drawer and assignee picker it opened (TPT647).
    if (state.subViewCleanup) state.subViewCleanup();
    // (TPT9099) Also closes its own Delete confirm; a request still queued is never sent.
    if (bulkToolbar) { bulkToolbar.destroy(); bulkToolbar = null; }
    closeModal(state.modalToken);
    // (TPT112) #content outlives this route — see renderActiveView() above.
    getContentEl().classList.remove('board-page--kanban');
    resetState();
  };
}
